import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import WelcomeSplash from "./pages/WelcomeSplash";
import Welcome from "./pages/Welcome";
import ProfileSetup from "./pages/ProfileSetup";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import EditProfile from "./pages/EditProfile";
import Tracker from "./pages/Tracker";
import SleepTracker from "./pages/SleepTracker";
import SOSToolkit from "./pages/SOSToolkit";
import LearningHub from "./pages/LearningHub";
import MicroLearning from "./pages/MicroLearning";
import SymptomExplorer from "./pages/SymptomExplorer";
import MythBuster from "./pages/MythBuster";
import AllTheGoss from "./pages/AllTheGoss";
import ArticleAdmin from "./pages/ArticleAdmin";
import Progress from "./pages/Progress";
import Insights from "./pages/Insights";
import Community from "./pages/Community";
import CommunityPost from "./pages/CommunityPost";
import Profile from "./pages/Profile";
import PersonalPlan from "./pages/PersonalPlan";
import Pricing from "./pages/Pricing";
import Shop from "./pages/Shop";
import Mugs from "./pages/shop/Mugs";
import Candles from "./pages/shop/Candles";
import Tees from "./pages/shop/Tees";
import Sweatshirts from "./pages/shop/Sweatshirts";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import CookiePolicy from "./pages/CookiePolicy";
import EditorialStandards from "./pages/EditorialStandards";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/AdminDashboard";
import Recipes from "./pages/Recipes";
import BannerGenerator from "./pages/BannerGenerator";
import ArticleDetail from "./pages/ArticleDetail";
import SocialMediaManager from "./pages/SocialMediaManager";
import Install from "./pages/Install";
import GossLanding from "./pages/features/GossLanding";
import TrackerLanding from "./pages/features/TrackerLanding";
import CommunityLanding from "./pages/features/CommunityLanding";
import AuraLanding from "./pages/features/AuraLanding";
import SleepLanding from "./pages/features/SleepLanding";
import PlanLanding from "./pages/features/PlanLanding";
import SymptomExplorerLanding from "./pages/features/SymptomExplorerLanding";
import ShopLanding from "./pages/features/ShopLanding";
import KnowledgeHub from "./pages/KnowledgeHub";
import KnowledgeTopic from "./pages/KnowledgeTopic";
import HRTGuide from "./pages/HRTGuide";
import { ScrollToTop } from "./components/ScrollToTop";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <SubscriptionProvider>
          <Routes>
            {/* Public routes - no subscription required */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/editorial-standards" element={<EditorialStandards />} />
            <Route path="/splash" element={<WelcomeSplash />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/profile-setup" element={<ProfileSetup />} />
            <Route path="/install" element={<Install />} />

            {/* Home is the public shopfront */}
            <Route path="/" element={<Index />} />

            {/* Account routes - sign-in required, no entitlement needed */}
            <Route path="/edit-profile" element={<ProtectedRoute authOnly><EditProfile /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute authOnly><Onboarding /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute authOnly><Profile /></ProtectedRoute>} />
            <Route path="/article-admin" element={<ProtectedRoute authOnly><ArticleAdmin /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute authOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/banner-generator" element={<ProtectedRoute authOnly><BannerGenerator /></ProtectedRoute>} />
            <Route path="/social-media" element={<ProtectedRoute authOnly><SocialMediaManager /></ProtectedRoute>} />

            {/* Member features - sign-in + active access required */}
            <Route path="/tracker" element={<ProtectedRoute><Tracker /></ProtectedRoute>} />
            <Route path="/sleep-tracker" element={<ProtectedRoute><SleepTracker /></ProtectedRoute>} />
            <Route path="/sos-toolkit" element={<ProtectedRoute><SOSToolkit /></ProtectedRoute>} />
            <Route path="/learning" element={<ProtectedRoute><LearningHub /></ProtectedRoute>} />
            <Route path="/micro-learning" element={<ProtectedRoute><MicroLearning /></ProtectedRoute>} />
            <Route path="/symptom-explorer" element={<ProtectedRoute><SymptomExplorer /></ProtectedRoute>} />
            <Route path="/symptom-explorer/:slug" element={<ProtectedRoute><SymptomExplorer /></ProtectedRoute>} />
            <Route path="/myth-buster" element={<ProtectedRoute><MythBuster /></ProtectedRoute>} />
            <Route path="/all-the-goss" element={<ProtectedRoute><AllTheGoss /></ProtectedRoute>} />
            <Route path="/article/:slug" element={<ProtectedRoute><ArticleDetail /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
            <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
            <Route path="/community/post/:id" element={<ProtectedRoute><CommunityPost /></ProtectedRoute>} />
            <Route path="/plan" element={<ProtectedRoute><PersonalPlan /></ProtectedRoute>} />
            <Route path="/recipes" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />

            {/* Shop stays public */}
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/mugs" element={<Mugs />} />
            <Route path="/shop/candles" element={<Candles />} />
            <Route path="/shop/tees" element={<Tees />} />
            <Route path="/shop/sweatshirts" element={<Sweatshirts />} />
            <Route path="/product/:handle" element={<ProductDetail />} />


            {/* Feature landing pages */}
            <Route path="/features/goss" element={<GossLanding />} />
            <Route path="/features/tracker" element={<TrackerLanding />} />
            <Route path="/features/community" element={<CommunityLanding />} />
            <Route path="/features/aura" element={<AuraLanding />} />
            <Route path="/features/sleep" element={<SleepLanding />} />
            <Route path="/features/plan" element={<PlanLanding />} />
            <Route path="/features/symptom-explorer" element={<SymptomExplorerLanding />} />
            <Route path="/features/shop" element={<ShopLanding />} />

            {/* Knowledge platform */}
            <Route path="/knowledge" element={<ProtectedRoute><KnowledgeHub /></ProtectedRoute>} />
            <Route path="/knowledge/:slug" element={<ProtectedRoute><KnowledgeTopic /></ProtectedRoute>} />
            <Route path="/learning/hrt-guide" element={<ProtectedRoute><HRTGuide /></ProtectedRoute>} />

            
            
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </SubscriptionProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
